// Three module cards where the reference has articles, then real testimonials in
// the same card language. REFERENCE.md §15 Insights, spec section 10.
//
// We have no blog and invent no articles: no dates and no read times. The tag comes
// from MODULES in copy.js, the title and text from day.items by `chapter`, and each
// card opens its chapter. Testimonials render only when the CMS has real ones.

import { useRef } from "react";
import { ArrowLeft, Quote } from "lucide-react";
import SectionHead from "./SectionHead";
import { Capture } from "./media";
import { MODULES } from "./copy";
import useAutoRail from "./motion/useAutoRail";
import "./Insights.css";

const CARD_SIZES = "(min-width: 1024px) 25vw, (min-width: 640px) 46vw, 75vw";

export default function Insights({ day, testimonials, hasTestimonials }) {
    const modulesRail = useRef(null);
    const voicesRail = useRef(null);
    const voices = hasTestimonials ? testimonials.items : [];

    useAutoRail(modulesRail);
    useAutoRail(voicesRail, { trigger: voices.length });

    const chapter = (id) => (day.items || []).find((it) => it.id === id);
    const cards = MODULES.items.filter((m) => chapter(m.chapter));

    return (
        <>
            {cards.length ? (
                <section className="ld-section ld-section--day ld-modules" id="modules" aria-labelledby="ld-modules-title">
                    <div className="ld-container">
                        <SectionHead
                            id="ld-modules-title"
                            title={MODULES.title}
                            aside={<a className="ld-ghost" href="#day">{MODULES.more}</a>}
                        />
                        <ul className="ld-rail ld-mcards" ref={modulesRail} data-ld="cards">
                            {cards.map((m) => {
                                const { title, body } = chapter(m.chapter);
                                return (
                                    <li key={m.chapter}>
                                        <article className="ld-mcard">
                                            <div className="ld-mcard__media">
                                                <Capture
                                                    name={m.capture}
                                                    alt=""
                                                    sizes={CARD_SIZES}
                                                    style={{ "--ld-focal": m.focal, "--ld-zoom": m.zoom }}
                                                />
                                            </div>
                                            <span className="ld-mcard__tag">{m.tag}</span>
                                            <h3 className="ld-mcard__title">
                                                <a href={`#day-${m.chapter}`}>{title}</a>
                                            </h3>
                                            <p className="ld-mcard__excerpt">{body}</p>
                                            <span className="ld-mcard__read" aria-hidden="true">
                                                {MODULES.linkLabel}
                                                <ArrowLeft />
                                            </span>
                                        </article>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </section>
            ) : null}

            {hasTestimonials ? (
                <section className="ld-section ld-section--day ld-section--flush ld-voices" id="testimonials" aria-labelledby="ld-testimonials-title">
                    <div className="ld-container">
                        <SectionHead id="ld-testimonials-title" eyebrow={testimonials.eyebrow} title={testimonials.title} lead={testimonials.subtitle} />
                        <ul className="ld-rail ld-mcards" ref={voicesRail} data-ld="cards">
                            {voices.map((review) => (
                                <li key={review.id || review.name}>
                                    <figure className="ld-voice">
                                        <span className="ld-disc ld-disc--sm"><Quote aria-hidden="true" /></span>
                                        <blockquote className="ld-voice__text">{review.review}</blockquote>
                                        <figcaption className="ld-voice__who">
                                            <strong>{review.name}</strong>
                                            {review.company ? <span>{review.company}</span> : null}
                                        </figcaption>
                                    </figure>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>
            ) : null}
        </>
    );
}
